import React, { useMemo, useRef, useState, useEffect, memo } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns-tz';
import { Spinner, Icon, contrastChart } from '@iq/react-components';
import classnames from 'classnames';

import { getActiveSite } from '../../../../../bundles/sites';
import { useSubscription, useRefetchOnRangeChange } from '../../../../../bundles/visualizations';
import { getTimezone } from '../../../../../bundles/application';
import { getSiteVariablesIndex } from '../../../../../bundles/sources';
import { useClientSize } from '../../../../../utils';
import { utcToSite } from '../../../../../datetimeUtils';
import { format as unitFormat, resolveVariableUnit } from '../../../../../units';
import { DropdownMenu } from '../../../../DropdownMenu';
import { darkenHex, arealines, axislabels, areas } from './helpers';
import Loader from '../../../../Loader';
import { resolveColor } from '../../../../../colors';

/**
 * Duval Triangle: diagnoses transformer faults from dissolved gas analysis (DGA).
 * The relative proportions of 3 dissolved gases are plotted inside a triangle,
 * and the colored zone the point lands in indicates the likely fault type
 * (PD, T1/T2/T3, D1/D2, DT, etc.).
 *
 * Supports Duval types 1, 4 and 5, which use different gas triplets:
 *   - Type 1: CH4 / C2H4 / C2H2
 *   - Type 4: H2  / CH4  / C2H6
 *   - Type 5: CH4 / C2H4 / C2H6
 */

const DuvalVisualization = memo(
  ({
    visualization,
    panelId,
    pollingDateRange,
    onExportData,
    onFullscreen,
    isPreview,
    isFullscreen,
    selected,
    onSelect,
    openSignalViewer,
    CustomTooltip,
    getMoreOptions,
    dropdownPlacement,
    signalCounts,
    visualizationName,
    hideToolbar,
  }) => {
    const site = useSelector(getActiveSite);
    const timezone = useSelector(getTimezone);
    const variablesIndex = useSelector(getSiteVariablesIndex);

    const [legendHeight, setLegendHeight] = useState(0);

    const [errors, setErrors] = useState([]);

    const [isAreaHighlighted, setIsAreaHighlighted] = useState({});

    const [showAreaTooltip, setShowAreaTooltip] = useState(false);
    const [areaTooltipPos, setAreaTooltipPos] = useState({ x: 0, y: 0 });
    const [areaTooltipText, setAreaTooltipText] = useState('');

    const [showPointTooltip, setShowPointTooltip] = useState(false);
    const [pointTooltipPos, setPointTooltipPos] = useState({ x: 0, y: 0 });
    const [pointTooltipData, setPointTooltipData] = useState({
      time: 0,
    });

    const {
      initialLoaded,
      seriesValues: values,
      loading,
    } = useSubscription(visualization.id, panelId);
    const { variables = [], configuration = {} } = visualization;
    const [{ height, width }, clientReference] = useClientSize();
    const legendReference = useRef(null);

    const addLegendHeight = configuration.legend?.show ? -legendHeight : 0;
    const addIsPreviewHeight = isPreview ? 0 : -53.5;
    const addErrorHeight = errors.length > 0 ? -32 : 0;

    const largestSize = Math.min(width, height);

    const vizSize =
      largestSize && largestSize + addLegendHeight + addIsPreviewHeight + addErrorHeight - 20;

    const L = vizSize / (1 / 2) / Math.sqrt(3);
    const W = L;

    useEffect(() => {
      if (legendReference.current) {
        setLegendHeight(legendReference.current.clientHeight);
      }
    }, []);

    useRefetchOnRangeChange({
      visualization,
      panelId,
      initialLoaded,
      selected,
      rangeKey: `${pollingDateRange.startDate}:${pollingDateRange.endDate}`,
    });

    const data = useMemo(() => {
      if (!values) return [];
      const hasConfig = variables.some((variable) =>
        values.some((value) => value.variable === variable.id)
      );
      if (!hasConfig) return [];
      return values.reduce((acc, { values: dataValues, name, granularity }) => {
        for (const [time, value, quality] of dataValues) {
          const parsedValue = Number.parseFloat(value);
          const cleanedValue = Number.isNaN(parsedValue) ? undefined : parsedValue;

          let axisAlignedValue = cleanedValue;

          if (quality !== 1) axisAlignedValue = null;

          const index = acc.findIndex((entry) => entry.time === time);
          if (index === -1) {
            const insertIndex = acc.findIndex(
              (_, index_) => acc[index_].time > time && (acc[index_ - 1] || {}).time < time
            );
            if (insertIndex === -1) {
              acc.push({
                time,
                [name]: axisAlignedValue,
                granularity,
              });
            } else {
              acc = [
                ...acc.slice(0, insertIndex),
                {
                  time,
                  [name]: axisAlignedValue,
                  granularity,
                },
                ...acc.slice(insertIndex),
              ];
            }
          } else {
            acc[index] = {
              ...acc[index],
              [name]: axisAlignedValue,
            };
          }
        }
        return acc;
      }, []);
    }, [values, variables, variablesIndex, isPreview]);

    const series = useMemo(
      () =>
        variables.map(({ id, aggregate, label, unit, unitLabel, decimals }) => {
          const { name } = (variablesIndex || {})[id] || {};
          const seriesId = `${id}.${aggregate}`;
          const { displayUnit, customUnit } = resolveVariableUnit(
            unit,
            unitLabel,
            (variablesIndex || {})[id]?.unit
          );

          return {
            variableId: id,
            id: seriesId,
            label: label || name,
            decimals: decimals || 'auto',
            unit: displayUnit,
            customUnit,
            aggregate,
          };
        }),
      [variables, variablesIndex, configuration]
    );

    const rollingAverageData = useMemo(
      () =>
        configuration.plots?.map((plot) => {
          let Xrolling = 0;
          let Yrolling = 0;

          return {
            color: resolveColor(plot.rollingAverageColor),
            data:
              series.length >= 3 &&
              data
                ?.filter((d) => d[plot.leftId] && d[plot.rightId] && d[plot.bottomId])
                .map((value, index) => {
                  const left = value[plot.leftId];
                  const right = value[plot.rightId];
                  const bottom = value[plot.bottomId];

                  const X =
                    0 +
                    L * (right / (right + left + bottom) + (left / (left + bottom + right)) * 0.5);
                  const Y = vizSize - (left / (left + bottom + right)) * vizSize;

                  Xrolling =
                    index === 0
                      ? X
                      : configuration.rollingAverage.decay * Xrolling +
                        (1 - configuration.rollingAverage.decay) * X;

                  Yrolling =
                    index === 0
                      ? Y
                      : configuration.rollingAverage.decay * Yrolling +
                        (1 - configuration.rollingAverage.decay) * Y;
                  return { X: Xrolling, Y: Yrolling };
                }),
          };
        }),
      [configuration.rollingAverage, data, L]
    );
    const tooltipLabelFormatter = (time) => {
      const dateFormat = 'yyyy-MM-dd, HH:mm:ss';
      return format(utcToSite(time, timezone), dateFormat, { timeZone: timezone });
    };

    function DrawAreaLine({ from, to }) {
      return (
        <line
          x1={0 + L * (from.P2 + from.P1 * 0.5)}
          y1={vizSize - from.P1 * vizSize}
          x2={0 + L * (to.P2 + to.P1 * 0.5)}
          y2={vizSize - to.P1 * vizSize}
        />
      );
    }

    function DrawArea({ points, fill, name = 'No name provided' }) {
      let pointsString = '';
      for (const point of points) {
        pointsString += `${0 + L * (point.P2 + point.P1 * 0.5)},${vizSize - point.P1 * vizSize} `;
      }

      // This is for checking wether the latest point is inside the area or not
      const polyCorners = points.length;
      const polyX = points.map((point) => 0 + L * (point.P2 + point.P1 * 0.5));
      const polyY = points.map((point) => vizSize - point.P1 * vizSize);

      const pointInPolygon = ({ left, right, bottom }) => {
        const P1 = left / (left + right + bottom);
        const P2 = right / (left + right + bottom);

        const x = 0 + L * (P2 + P1 * 0.5);
        const y = vizSize - P1 * vizSize;
        let index = polyCorners - 1;

        let oddNodes = false;

        for (let index_ = 0; index_ < polyCorners; index_ += 1) {
          if (
            ((polyY[index_] < y && polyY[index] >= y) ||
              (polyY[index] < y && polyY[index_] >= y)) &&
            polyX[index_] +
              ((y - polyY[index_]) / (polyY[index] - polyY[index_])) *
                (polyX[index] - polyX[index_]) <
              x
          ) {
            oddNodes = !oddNodes;
          }
          index = index_;
        }

        return oddNodes;
      };

      useEffect(() => {
        if (data.length === 0 && !configuration.plots) {
          return;
        }
        const pointIsInArea = configuration.plots?.some((plot) => {
          const filteredData = data.filter(
            (d) => d[plot.leftId] && d[plot.rightId] && d[plot.bottomId]
          );
          if (filteredData.length === 0) return false;
          return pointInPolygon({
            left: filteredData.at(-1)[plot.leftId],
            right: filteredData.at(-1)[plot.rightId],
            bottom: filteredData.at(-1)[plot.bottomId],
          });
        });
        if (pointIsInArea && !isAreaHighlighted[name.slice(0, 2)]) {
          setIsAreaHighlighted((state) => ({ ...state, [name.slice(0, 2)]: true }));
        } else if (!pointIsInArea && isAreaHighlighted[name.slice(0, 2)]) {
          setIsAreaHighlighted((state) => ({ ...state, [name.slice(0, 2)]: false }));
        }
      }, []);

      function showTooltip(event) {
        if (showAreaTooltip || showPointTooltip) {
          return;
        }
        setAreaTooltipPos({ x: event.clientX, y: event.clientY });
        setAreaTooltipText(name);
        setShowAreaTooltip(true);
      }

      return (
        <>
          <polygon
            points={pointsString}
            fill={fill}
            className="area"
            onMouseOut={() => {
              setShowAreaTooltip(false);
            }}
            onMouseOver={showTooltip}
          />
        </>
      );
    }

    function DrawAreaHighlight({ points, show = false, fill = 'none', filter = '' }) {
      let pointsString = '';
      for (const point of points) {
        pointsString += `${0 + L * (point.P2 + point.P1 * 0.5)},${vizSize - point.P1 * vizSize} `;
      }

      return (
        <>
          <polygon
            style={{ pointerEvents: 'none' }}
            points={pointsString}
            strokeWidth={show ? '1' : '0'}
            stroke={show ? darkenHex(fill, 80) : null}
            fill={show ? fill : 'none'}
            filter={show ? filter : ''}
          />
        </>
      );
    }
    function TickLine({ P1, P2, axis }) {
      let P1Factor = 0;
      let P2Factor = 0;
      switch (axis) {
        case 'p1': {
          P2Factor = -0.01;
          break;
        }
        case 'p2': {
          P1Factor = 0.01;
          break;
        }
        case 'p3': {
          P1Factor = -0.01;
          P2Factor = 0.01;
          break;
        }
        default: {
          break;
        }
      }

      return (
        <>
          <line
            x1={`${0 + L * (P2 - P2Factor + (P1 - P1Factor) * 0.5)}`}
            y1={`${vizSize - (P1 - P1Factor) * vizSize}`}
            x2={`${0 + L * (P2 + P2Factor + (P1 + P1Factor) * 0.5)}`}
            y2={`${vizSize - (P1 + P1Factor) * vizSize}`}
            className="tick-line"
          />
        </>
      );
    }

    const PointPlot = ({ plot, duvalMode }) => {
      const leftSerie = series.find((serie) => serie.id === plot.leftId);
      const rightSerie = series.find((serie) => serie.id === plot.rightId);
      const bottomSerie = series.find((serie) => serie.id === plot.bottomId);
      if (!leftSerie || !rightSerie || !bottomSerie) {
        return null;
      }

      const filteredData = data.filter(
        (d) => d[leftSerie.id] && d[rightSerie.id] && d[bottomSerie.id]
      );
      if (filteredData.length === 0) {
        return null;
      }
      const left = leftSerie && filteredData.at(-1)[leftSerie.id];
      const right = rightSerie && filteredData.at(-1)[rightSerie.id];
      const bottom = bottomSerie && filteredData.at(-1)[bottomSerie.id];
      switch (duvalMode) {
        case 'current': {
          return (
            <Point
              P1={left / (left + right + bottom)}
              P2={right / (left + right + bottom)}
              color={resolveColor(plot.color)}
              length={0}
              last={true}
              left={left}
              bottom={bottom}
              right={right}
              leftUnit={leftSerie.unit}
              bottomUnit={bottomSerie.unit}
              rightUnit={rightSerie.unit}
              label={plot.label}
              time={filteredData.at(-1).time}
            />
          );
        }
        case 'over time': {
          return (
            <OverTimePointPlot
              label={plot.label}
              data={data}
              leftSerie={leftSerie}
              rightSerie={rightSerie}
              bottomSerie={bottomSerie}
              color={resolveColor(plot.color)}
            />
          );
        }
        default: {
          return null;
        }
      }
    };

    function Point({
      P1,
      P2,
      idx: index = 0,
      left = 'Missing',
      right = 'Missing',
      bottom = 'Missing',
      color = 0,
      last = false,
      length = 1,
      time = 0,
    }) {
      if (left === 0 && right === 0 && bottom === 0 && errors.length === 0) {
        setErrors((state) => [
          ...state,
          `All recieved values are 0. - ${tooltipLabelFormatter(time)}`,
        ]);
      }
      if ((left < 0 || right < 0 || bottom < 0) && errors.length === 0) {
        setErrors((state) => [
          ...state,
          `Negative value recieved. - ${tooltipLabelFormatter(time)}`,
        ]);
      }

      return (
        <>
          {last && configuration.lines && configuration.lines.show && (
            <g className="point-lines">
              <line
                x1={`${0 + L * (P2 + P1 * 0.5)}`}
                y1={`${vizSize - P1 * vizSize}`}
                x2={`${0 + L * (0 + P1 * 0.5)}`}
                y2={`${vizSize - P1 * vizSize}`}
                strokeDasharray={'4 4'}
              />
              <line
                x1={`${0 + L * (P2 + P1 * 0.5)}`}
                y1={`${vizSize - P1 * vizSize}`}
                x2={`${0 + L * (P2 + (1 - P2) * 0.5)}`}
                y2={`${vizSize - (1 - P2) * vizSize}`}
                strokeDasharray={'4 4'}
              />
              <line
                x1={`${0 + L * (P2 + P1 * 0.5)}`}
                y1={`${vizSize - P1 * vizSize}`}
                x2={`${0 + L * (P2 + P1 + 0 * 0.5)}`}
                y2={`${vizSize - 0 * vizSize}`}
                strokeDasharray={'4 4'}
              />
              <line />
            </g>
          )}
          <circle
            r={last ? configuration.pointRadius + 2 : configuration.pointRadius}
            cx={`${0 + L * (P2 + P1 * 0.5)}`}
            cy={`${vizSize - P1 * vizSize}`}
            className="duval-point"
            strokeWidth={2}
            stroke={
              (time === pointTooltipData.time && showPointTooltip && 'black') ||
              (last && 'white') ||
              ''
            }
            style={{ opacity: `${(100 / length) * (index + 1)}%` }}
            fill={color}
            onMouseOut={() => {
              setShowPointTooltip(false);
            }}
            onMouseOver={(event) => {
              if (showPointTooltip || showAreaTooltip) {
                return;
              }
              setPointTooltipPos({ x: event.clientX + 10, y: event.clientY + 10 });

              setPointTooltipData({
                time,
              });
              setShowPointTooltip(true);
            }}
          />
        </>
      );
    }

    const OverTimePointPlot = ({
      leftSerie = null,
      rightSerie = null,
      bottomSerie = null,
      color,
      label = '',
    }) => {
      // Filtering out missing datapoints which cannot be plotted on the duval
      const filteredData = data.filter(
        (d) => d[leftSerie.id] && d[rightSerie.id] && d[bottomSerie.id]
      );
      return (
        leftSerie &&
        rightSerie &&
        bottomSerie &&
        filteredData.map((point, index) => {
          const left = point[leftSerie.id];
          const right = point[rightSerie.id];
          const bottom = point[bottomSerie.id];

          return (
            <Point
              P1={left / (left + right + bottom)}
              P2={right / (left + right + bottom)}
              color={color}
              length={filteredData.length}
              idx={index}
              key={index + leftSerie.id + rightSerie.id + bottomSerie.id}
              label={label}
              last={index === filteredData.length - 1}
              left={left}
              bottom={bottom}
              right={right}
              leftUnit={leftSerie.unit}
              bottomUnit={bottomSerie.unit}
              rightUnit={rightSerie.unit}
              time={point.time}
            />
          );
        })
      );
    };

    function getCurrentArea({ left, right, bottom }) {
      const currentArea = areas(configuration.duvalType || 1).find((area) => {
        const polyCorners = area.points.length;
        const polyX = area.points.map((point) => 0 + L * (point.P2 + point.P1 * 0.5));
        const polyY = area.points.map((point) => vizSize - point.P1 * vizSize);

        const P1 = left / (left + right + bottom);
        const P2 = right / (left + right + bottom);

        const x = 0 + L * (P2 + P1 * 0.5);
        const y = vizSize - P1 * vizSize;
        let index = polyCorners - 1;

        let oddNodes = false;

        for (let index_ = 0; index_ < polyCorners; index_ += 1) {
          if (
            ((polyY[index_] < y && polyY[index] >= y) ||
              (polyY[index] < y && polyY[index_] >= y)) &&
            polyX[index_] +
              ((y - polyY[index_]) / (polyY[index] - polyY[index_])) *
                (polyX[index] - polyX[index_]) <
              x
          ) {
            oddNodes = !oddNodes;
          }
          index = index_;
        }
        return oddNodes;
      });
      return currentArea.name.slice(0, 2);
    }

    const handleDownloadClick = () => {
      onExportData(data[0].granularity);
    };
    const handleSelect = () => {
      onSelect(visualization);
    };
    const classes = classnames('menu', {});

    const vizHeight = height - 28; // less header
    const loaderHeight = vizHeight - 28; // less loader padding

    if (series.length < signalCounts?.minSignal) {
      return (
        <div className="star-error-container">
          <Icon
            icon="he-info"
            size="s"
          />
          <span className="error-message">
            <span className="error-bold">{visualizationName}</span> visualizations require between
            <span className="error-bold"> {signalCounts?.minSignal} </span> and
            <span className="error-bold"> {signalCounts?.maxSignal} signals</span>
          </span>
        </div>
      );
    }

    const hasNumericData = data?.some((entry) =>
      Object.keys(entry).some(
        (key) =>
          key !== 'time' &&
          key !== 'granularity' &&
          typeof entry[key] === 'number' &&
          !Number.isNaN(entry[key])
      )
    );
    const isVizEmpty = !hasNumericData && !loading && initialLoaded;
    const showOptionsMenu = !isPreview && !isVizEmpty;

    return (
      <div
        className={`duval-visualization-component ${selected ? 'checked' : ''}`}
        ref={clientReference}
      >
        {!isPreview && !isFullscreen ? (
          <div
            className="duval-header"
            onDoubleClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="header-title truncateText">
              {!hideToolbar && (
                <div
                  className={`select ${selected ? 'checked' : ''}`}
                  onClick={handleSelect}
                >
                  <Icon
                    icon={`${selected ? 'he-checkbox-selected' : 'he-checkbox'}`}
                    size="s"
                  />
                </div>
              )}
              <CustomTooltip content={configuration?.title || visualization?.name}>
                <span className="title truncateText">
                  {configuration?.title || visualization?.name || '\u00A0'}
                </span>
              </CustomTooltip>
            </div>

            <div className={classes}>
              {showOptionsMenu && (
                <DropdownMenu
                  trigger="he-moreoptionsvertical"
                  triggerSize="s"
                  menuOptions={getMoreOptions({
                    site,
                    onFullscreen,
                    handleDownloadClick,
                    openSignalViewer,
                  })}
                  menuXPlacement={dropdownPlacement?.x}
                  menuYPlacement={dropdownPlacement?.y}
                  styles={{
                    dropdownIndicator: { padding: '0px' },
                    option: { cursor: 'pointer' },
                  }}
                />
              )}
            </div>

            {initialLoaded && loading && (
              <Spinner
                size="s"
                className="spinner"
              />
            )}
          </div>
        ) : (
          !isFullscreen && (
            <div className="duval-preview-title">
              <span className="title">{configuration.title || visualization.name || '\u00A0'}</span>
            </div>
          )
        )}
        {!initialLoaded && loading && data.length === 0 ? (
          <Loader
            text="Loading ..."
            height={loaderHeight}
          />
        ) : (
          <div className="duval-wrapper">
            {showAreaTooltip && (
              <div
                className="tooltip"
                style={{ left: areaTooltipPos.x, top: areaTooltipPos.y }}
              >
                {areaTooltipText}
              </div>
            )}
            {showPointTooltip && (
              <div
                // Positioning tooltip so it doesn't overflow screen
                ref={(element) => {
                  if (!element) return;

                  if (
                    window.innerWidth - pointTooltipPos.x - element.getBoundingClientRect().width <
                    0
                  ) {
                    setPointTooltipPos((state) => ({
                      ...state,
                      x: pointTooltipPos.x - element.getBoundingClientRect().width - 20,
                    }));
                  }

                  if (
                    window.innerHeight -
                      pointTooltipPos.y -
                      element.getBoundingClientRect().height <
                    0
                  ) {
                    setPointTooltipPos((state) => ({
                      ...state,
                      y: pointTooltipPos.y - element.getBoundingClientRect().height - 20,
                    }));
                  }
                }}
                className="tooltip"
                style={{
                  left: pointTooltipPos.x,
                  top: pointTooltipPos.y,
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th colSpan={5}> {tooltipLabelFormatter(pointTooltipData.time)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>Plot</th>
                      <th>{axislabels(configuration.duvalType || 1).left}</th>
                      <th>{axislabels(configuration.duvalType || 1).right}</th>
                      <th>{axislabels(configuration.duvalType || 1).base}</th>
                      <th>Area</th>
                    </tr>

                    {configuration.plots.map((plot, index) => {
                      const leftSerie = series.find((serie) => serie.id === plot.leftId);
                      const rightSerie = series.find((serie) => serie.id === plot.rightId);
                      const bottomSerie = series.find((serie) => serie.id === plot.bottomId);

                      if (!leftSerie || !rightSerie || !bottomSerie) {
                        return null;
                      }

                      const timeData = data.find((d) => d.time === pointTooltipData.time);
                      if (!timeData) {
                        return null;
                      }

                      const left = timeData[leftSerie.id];
                      const bottom = timeData[bottomSerie.id];
                      const right = timeData[rightSerie.id];
                      if (!left || !bottom || !right) {
                        return null;
                      }
                      return (
                        <tr
                          style={{
                            color: resolveColor(plot.color),
                          }}
                          key={index}
                        >
                          <td>{plot.label || 'No label'}</td>
                          <td>
                            {`${unitFormat(leftSerie.unit, left, leftSerie.decimals, leftSerie.customUnit).value} ${
                              unitFormat(leftSerie.unit, left, undefined, leftSerie.customUnit).unit
                            }`}
                          </td>
                          <td>
                            {`${unitFormat(rightSerie.unit, right, rightSerie.decimals, rightSerie.customUnit).value} ${
                              unitFormat(rightSerie.unit, right, undefined, rightSerie.customUnit)
                                .unit
                            }`}
                          </td>
                          <td>
                            {`${unitFormat(bottomSerie.unit, bottom, bottomSerie.decimals, bottomSerie.customUnit).value} ${
                              unitFormat(
                                bottomSerie.unit,
                                bottom,
                                undefined,
                                bottomSerie.customUnit
                              ).unit
                            }`}
                          </td>
                          <td>{getCurrentArea({ left, right, bottom })}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div width="100%">
              <div
                className="duval-container"
                onMouseOut={() => {
                  setShowAreaTooltip(false);
                  setShowPointTooltip(false);
                }}
              >
                <svg
                  id={`duval${visualization.id}`}
                  className="duval-surface"
                  width={W}
                  height={vizSize - 20 * -1}
                >
                  <g name="labels">
                    <defs>
                      <marker
                        className="axis-label-arrow"
                        id="arrow"
                        viewBox="0 0 10 10"
                        refX="3"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto-start-reverse"
                      >
                        <path d="M 0 0 L 10 5 L 0 10 L 3 5 z" />
                      </marker>
                    </defs>
                    <path
                      id={`LeftPath${visualization.id}${isPreview ? '2' : '1'}${isFullscreen}`}
                      d={`M ${0 + L * (-0.045 + 0.3 * 0.5)},${vizSize - 0.3 * vizSize}
                                  L ${0 + L * (-0.045 + 0.7 * 0.5)},${vizSize - 0.7 * vizSize}`}
                    />
                    <path
                      markerEnd="url(#arrow)"
                      className="axis-label-line"
                      d={`M ${0 + L * (0 - 0.032 * 0.5)},${vizSize - 0.032 * vizSize}
                                  L ${0 + L * (-0.032 + 1 * 0.5)},${vizSize - 1 * vizSize}`}
                    />
                    <text
                      textAnchor="middle"
                      className="axis-label"
                    >
                      <textPath
                        href={`#LeftPath${visualization.id}${isPreview ? '2' : '1'}${isFullscreen}`}
                        startOffset="55%"
                      >
                        {axislabels(configuration.duvalType || 1).left} %
                      </textPath>
                    </text>
                    <path
                      id={`RightPath${visualization.id}${isPreview ? '2' : '1'}${isFullscreen}`}
                      d={`M ${0 + L * (0.3 + 0.745 * 0.5)},${vizSize - 0.745 * vizSize}
                                  L ${0 + L * (0.7 + 0.345 * 0.5)},${vizSize - 0.345 * vizSize}`}
                    />
                    <path
                      markerEnd="url(#arrow)"
                      className="axis-label-line"
                      d={`M ${0 + L * (0.032 + 1 * 0.5)},${vizSize - 1 * vizSize}
                                  L ${0 + L * (1 + 0.032 * 0.5)},${vizSize - 0.032 * vizSize}`}
                    />
                    <text
                      textAnchor="middle"
                      className="axis-label"
                    >
                      <textPath
                        href={`#RightPath${visualization.id}${isPreview ? '2' : '1'}${isFullscreen}`}
                        startOffset="55%"
                      >
                        {axislabels(configuration.duvalType || 1).right} %
                      </textPath>
                    </text>
                    <path
                      id={`BottomPath${visualization.id}${isPreview ? '2' : '1'}${isFullscreen}`}
                      d={`M ${0 + L * (0.345 + -0.045 * 0.5)},${vizSize - -0.045 * vizSize}
                                  L ${0 + L * (0.745 + -0.045 * 0.5)},${vizSize - -0.045 * vizSize}`}
                    />
                    {0 + L * (0 + 0.032 * 0.5)},${vizSize - -0.032 * vizSize}
                    <path
                      markerEnd="url(#arrow)"
                      className="axis-label-line"
                      d={`M ${0 + L * (1 + -0.032 * 0.5)},${vizSize - -0.032 * vizSize}
                                  L ${0 + L * (0 + 0.032 * 0.5)},${vizSize - -0.032 * vizSize}`}
                    />
                    <text
                      textAnchor="middle"
                      transform="translate(0 11)"
                      className="axis-label"
                    >
                      <textPath
                        href={`#BottomPath${visualization.id}${isPreview ? '2' : '1'}${isFullscreen}`}
                        startOffset="45%"
                        textAnchor="middle"
                      >
                        {axislabels(configuration.duvalType || 1).base} %
                      </textPath>
                    </text>
                  </g>

                  <g name="Areas">
                    {areas(configuration.duvalType || 1).map((area) => (
                      <DrawArea
                        points={area.points}
                        fill={area.fill}
                        name={area.name}
                        key={area.name}
                      />
                    ))}
                  </g>

                  <g
                    className={`area-lines area-lines-stroke`}
                    name="area-lines"
                    strokeOpacity="0.5"
                    strokeWidth={1}
                  >
                    {arealines(configuration.duvalType || 1).map((line, index) => (
                      <DrawAreaLine
                        key={index}
                        from={line.from}
                        to={line.to}
                      />
                    ))}
                  </g>
                  {configuration.highlightAreas?.show && (
                    <g
                      name="Area-highlights"
                      className="area-highlights"
                    >
                      <defs>
                        <filter
                          id="highlightFilter"
                          width="120"
                          height="120"
                        >
                          <feFlood
                            result="flood"
                            floodColor="#ffffff"
                            floodOpacity="1"
                          ></feFlood>
                          <feComposite
                            in="flood"
                            result="mask"
                            in2="SourceGraphic"
                            operator="in"
                          ></feComposite>
                          <feMorphology
                            in="mask"
                            result="dilated"
                            operator="dilate"
                            radius="1"
                          ></feMorphology>
                          <feGaussianBlur
                            in="dilated"
                            result="blurred"
                            stdDeviation="1"
                          ></feGaussianBlur>
                          <feMerge>
                            <feMergeNode in="blurred"></feMergeNode>
                            <feMergeNode in="SourceGraphic"></feMergeNode>
                          </feMerge>
                        </filter>
                      </defs>
                      {areas(configuration.duvalType || 1).map((area) => (
                        <DrawAreaHighlight
                          points={area.points}
                          show={isAreaHighlighted[area.name.slice(0, 2)]}
                          key={area.name}
                          fill={area.fill}
                          filter="url(#highlightFilter)"
                        />
                      ))}
                    </g>
                  )}
                  <g
                    className="triangle-side"
                    fill="none"
                  >
                    <line
                      x1="0%"
                      y1={vizSize}
                      x2="50%"
                      y2="0%"
                    />
                    <line
                      x1="0%"
                      y1={vizSize}
                      x2="100%"
                      y2={vizSize}
                    />
                    <line
                      x1="100%"
                      y1={vizSize}
                      x2="50%"
                      y2="0%"
                    />
                  </g>
                  <g
                    name="Ticklines"
                    className="tick-lines"
                  >
                    <g>
                      <TickLine
                        P1={0.1}
                        P2={0}
                        axis="p1"
                      />
                      <TickLine
                        P1={0.2}
                        P2={0}
                        axis="p1"
                      />
                      <TickLine
                        P1={0.3}
                        P2={0}
                        axis="p1"
                      />
                      <TickLine
                        P1={0.4}
                        P2={0}
                        axis="p1"
                      />
                      <TickLine
                        P1={0.5}
                        P2={0}
                        axis="p1"
                      />
                      <TickLine
                        P1={0.6}
                        P2={0}
                        axis="p1"
                      />
                      <TickLine
                        P1={0.7}
                        P2={0}
                        axis="p1"
                      />
                      <TickLine
                        P1={0.8}
                        P2={0}
                        axis="p1"
                      />
                      <TickLine
                        P1={0.9}
                        P2={0}
                        axis="p1"
                      />
                    </g>

                    <g>
                      <TickLine
                        P1={0.9}
                        P2={0.1}
                        axis="p2"
                      />
                      <TickLine
                        P1={0.8}
                        P2={0.2}
                        axis="p2"
                      />
                      <TickLine
                        P1={0.7}
                        P2={0.3}
                        axis="p2"
                      />
                      <TickLine
                        P1={0.6}
                        P2={0.4}
                        axis="p2"
                      />
                      <TickLine
                        P1={0.5}
                        P2={0.5}
                        axis="p2"
                      />
                      <TickLine
                        P1={0.4}
                        P2={0.6}
                        axis="p2"
                      />
                      <TickLine
                        P1={0.3}
                        P2={0.7}
                        axis="p2"
                      />
                      <TickLine
                        P1={0.2}
                        P2={0.8}
                        axis="p2"
                      />
                      <TickLine
                        P1={0.1}
                        P2={0.9}
                        axis="p2"
                      />
                    </g>

                    <g>
                      <TickLine
                        P1={0}
                        P2={0.1}
                        axis="p3"
                      />
                      <TickLine
                        P1={0}
                        P2={0.2}
                        axis="p3"
                      />
                      <TickLine
                        P1={0}
                        P2={0.3}
                        axis="p3"
                      />
                      <TickLine
                        P1={0}
                        P2={0.4}
                        axis="p3"
                      />
                      <TickLine
                        P1={0}
                        P2={0.5}
                        axis="p3"
                      />
                      <TickLine
                        P1={0}
                        P2={0.6}
                        axis="p3"
                      />
                      <TickLine
                        P1={0}
                        P2={0.7}
                        axis="p3"
                      />
                      <TickLine
                        P1={0}
                        P2={0.8}
                        axis="p3"
                      />
                      <TickLine
                        P1={0}
                        P2={0.9}
                        axis="p3"
                      />
                    </g>
                  </g>

                  {configuration.plots &&
                    configuration.plots.map((plot, index) => (
                      <PointPlot
                        key={index}
                        plot={plot}
                        duvalMode={configuration.duvalMode || ''}
                      />
                    ))}

                  {configuration.rollingAverage?.show && (
                    <g>
                      {rollingAverageData?.map((rollingPlot, index) => (
                        <g key={index}>
                          <g
                            name="rolling-plot"
                            style={{ pointerEvents: 'none' }}
                          >
                            {rollingPlot.data.length > 0 &&
                              rollingPlot.data.map((value, index) => {
                                if (configuration.rollingAverage?.type === 'Lines') {
                                  if (index === rollingPlot.data.length - 1) {
                                    return null;
                                  }
                                  return (
                                    <line
                                      key={index}
                                      x1={value.X}
                                      y1={value.Y}
                                      x2={rollingPlot.data[index + 1].X}
                                      y2={rollingPlot.data[index + 1].Y}
                                      stroke={resolveColor(rollingPlot.color) || contrastChart[1]}
                                      strokeWidth={configuration.pointRadius / 3}
                                      opacity={((100 / rollingPlot.data.length) * index) / 100}
                                    />
                                  );
                                }
                                if (configuration.rollingAverage?.type === 'Both') {
                                  return (
                                    <g key={index}>
                                      {index !== rollingPlot.data.length - 1 && (
                                        <line
                                          x1={value.X}
                                          y1={value.Y}
                                          x2={rollingPlot.data[index + 1].X}
                                          y2={rollingPlot.data[index + 1].Y}
                                          stroke={
                                            resolveColor(rollingPlot.color) || contrastChart[1]
                                          }
                                          strokeWidth={configuration.pointRadius / 3}
                                          opacity={((100 / rollingPlot.data.length) * index) / 100}
                                        />
                                      )}
                                      <circle
                                        cx={value.X}
                                        cy={value.Y}
                                        r={configuration.pointRadius / 2}
                                        opacity={((100 / rollingPlot.data.length) * index) / 100}
                                        fill={resolveColor(rollingPlot.color) || contrastChart[1]}
                                        stroke=""
                                      />
                                    </g>
                                  );
                                }

                                return (
                                  <circle
                                    key={index}
                                    cx={value.X}
                                    cy={value.Y}
                                    r={configuration.pointRadius / 2}
                                    opacity={((100 / rollingPlot.data.length) * index) / 100}
                                    fill={resolveColor(rollingPlot.color) || contrastChart[1]}
                                    stroke=""
                                  />
                                );
                              })}
                          </g>
                        </g>
                      ))}
                    </g>
                  )}
                </svg>

                {errors.length > 0 ? (
                  <div style={{ textAlign: 'center', maxWidth: width }}>
                    Signal Error: {errors[0]}
                  </div>
                ) : null}
                {configuration.legend && configuration.legend.show && (
                  <div
                    className="duval-legend"
                    ref={legendReference}
                  >
                    {visualization.configuration.plots &&
                      visualization.configuration.plots.map((plot, index) => (
                        <div
                          key={index}
                          className="duval-legend-item"
                        >
                          <svg
                            width="10"
                            height="10"
                            style={{ marginRight: '0.5rem' }}
                          >
                            <rect
                              width="10"
                              height="10"
                              fill={resolveColor(plot.color)}
                            />
                          </svg>
                          {plot.label}
                        </div>
                      ))}
                    {configuration.rollingAverage?.show &&
                      configuration.plots?.map((plot, index) => (
                        <div
                          key={index}
                          className="duval-legend-item"
                        >
                          <svg
                            width="10"
                            height="10"
                            style={{ marginRight: '0.5rem' }}
                          >
                            <rect
                              width="10"
                              height="10"
                              fill={resolveColor(plot.rollingAverageColor)}
                            />
                          </svg>
                          {plot.label} rolling average {`(${configuration.rollingAverage.decay})`}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
  (previousProperties, properties) =>
    JSON.stringify(previousProperties) === JSON.stringify(properties)
);

export default DuvalVisualization;
